import { useState } from 'react';
import round from '../../../helpers/round';
import { Alert, Typography, Box, IconButton, Collapse, Tooltip, useMediaQuery } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Link from '@mui/material/Link';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';

const HansenResultBcFactors = ({ result }) => {
  const { canUserSeeCalculationSteps, isPhiZero, phips, nc, nq, nGamma } = result.output;
  const isSmallScreen = useMediaQuery('(max-width:700px)');
  const [showNqDetails, setShowNqDetails] = useState(false);
  const [showNcDetails, setShowNcDetails] = useState(false);
  const [showNgammaDetails, setShowNgammaDetails] = useState(false);

  return (
    <Box sx={{ my: 2, textAlign: 'justify' }}>
      <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
        <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
          Calculation of Hansen Bearing Capacity Factors
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
          This section computes the Hansen bearing capacity factors used in the final capacity equations, including N<sub>q</sub>,
          N<sub>c</sub>, and N<sub>&gamma;</sub>. The active formulas depend on the friction-angle conditions, including the &phi;
          = 0 special case.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Success Alert: Surcharge factor (Nq) */}
          {!isPhiZero && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowNqDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showNqDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>
                  Surcharge factor (N<sub>q</sub>) = {round(nq, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showNqDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    N<sub>q</sub>=e<sup>&pi;tan&phi;</sup>tan<sup>2</sup>(45+&phi;/2)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    N<sub>q</sub>=e<sup>&pi;tan{round(phips, 2)}</sup>tan<sup>2</sup>(45+{round(phips, 2)}/2)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    N<sub>q</sub>={round(nq, 2)}
                  </Typography>
                </Collapse>
              )}
            </Alert>
          )}
          {/* Success Alert: Cohesion factor (Nc) */}
          <Alert
            icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
            severity="success"
            variant="outlined"
            sx={{
              backgroundColor: 'rgba(46,125,50,0.04)',
              alignItems: 'flex-start', // keep icon & action pinned to top
              '& .MuiAlert-icon': {
                alignSelf: 'flex-start',
                mt: 0.25,
                minWidth: 24, // fixed width for left icon column
              },
              '& .MuiAlert-action': {
                alignSelf: 'flex-start',
                mt: 0.25,
                // fix the action column width so the button never shifts
                width: 32,
                justifyContent: 'center',
                p: 0,
                ml: 1,
              },
              '& .MuiAlert-message': { width: '100%' }, // let message grow downward
            }}
            action={
              canUserSeeCalculationSteps ? (
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => setShowNcDetails((v) => !v)}
                  sx={{ width: 32, height: 32 }} // matches action width above
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transform: showNcDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.15s',
                    }}
                  />
                </IconButton>
              ) : (
                <Tooltip
                  title={
                    <span>
                      Upgrade to unlock calculation details.{' '}
                      <Link
                        href="/pricing"
                        target="_blank"
                        rel="noopener"
                        style={{ color: '#90caf9', textDecoration: 'underline' }}
                      >
                        See plans
                      </Link>
                    </span>
                  }
                  leaveDelay={500}
                  arrow
                >
                  <LockOutlinedIcon
                    sx={{
                      fontSize: 20,
                      color: 'text.disabled',
                      cursor: 'not-allowed',
                      mr: 1,
                      mt: 0.5,
                    }}
                  />
                </Tooltip>
              )
            }
          >
            <Typography
              variant="body2"
              color="text.secondary"
              component="div"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <b>
                Cohesion factor (N<sub>c</sub>) = {round(nc, 2)}
              </b>
            </Typography>

            {canUserSeeCalculationSteps && (
              <Collapse in={showNcDetails} unmountOnExit>
                {isPhiZero && (
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    &phi; = 0 &rarr; Only N<sub>c</sub> should be calculated.
                  </Typography>
                )}
                {!isPhiZero ? (
                  <>
                    <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                      N<sub>c</sub>=(N<sub>q</sub>-1) cot&phi;
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      N<sub>c</sub>=({round(nq, 2)}-1)cot
                      {round(phips, 2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      N<sub>c</sub>={round(nc, 2)}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      N<sub>c</sub>=&pi;+2
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      N<sub>c</sub>={round(nc, 2)}{' '}
                    </Typography>
                  </>
                )}
              </Collapse>
            )}
          </Alert>
          {/* Success Alert: Unit weight factor (Ngamma) */}
          {!isPhiZero && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowNgammaDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showNgammaDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>
                  Unit weight factor (N<sub>&gamma;</sub>) = {round(nGamma, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showNgammaDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    N<sub>&gamma;</sub>=1.5(N<sub>q</sub>-1)tan(&phi;)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    N<sub>&gamma;</sub>=1.5({round(nq, 2)}-1)tan({round(phips, 2)})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    N<sub>&gamma;</sub>={round(nGamma, 2)}
                  </Typography>
                </Collapse>
              )}
            </Alert>
          )}
        </Box>
      </Alert>
    </Box>
  );
};
export default HansenResultBcFactors;
